import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

export type AppLanguage = 'en' | 'da';

const localeLoaders: Record<AppLanguage, () => Promise<{ default: Record<string, unknown> }>> = {
  en: () => import('./locales/en.json'),
  da: () => import('./locales/da.json'),
};

function isChaosKitchenDkHost(): boolean {
  if (typeof window === 'undefined') return true;
  const host = window.location.hostname.toLowerCase();
  return host === 'chaoskitchen.dk' || host === 'www.chaoskitchen.dk' || host.endsWith('.chaoskitchen.dk');
}

function langFromQuery(): AppLanguage | null {
  if (typeof window === 'undefined') return null;
  try {
    const param = new URLSearchParams(window.location.search).get('lang');
    if (!param) return null;
    if (param.toLowerCase().startsWith('da')) return 'da';
    if (param.toLowerCase().startsWith('en')) return 'en';
  } catch {
    // ignore
  }
  return null;
}

/** Default Danish on .dk so Danish search traffic lands on a Danish site. */
function detectInitialLanguage(): AppLanguage {
  const fromQuery = langFromQuery();
  if (fromQuery) return fromQuery;

  try {
    const stored = localStorage.getItem('i18nextLng');
    if (stored?.toLowerCase().startsWith('da')) return 'da';
    if (stored?.toLowerCase().startsWith('en')) return 'en';
  } catch {
    // ignore
  }

  if (typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('da')) {
    return 'da';
  }

  // chaoskitchen.dk → Danish first paint for crawlers and new visitors
  if (isChaosKitchenDkHost()) return 'da';

  return 'en';
}

let bootstrapped = false;

export async function bootstrapI18n(): Promise<typeof i18n> {
  if (bootstrapped) return i18n;
  bootstrapped = true;

  const initialLng = detectInitialLanguage();
  const primary = (await localeLoaders[initialLng]()).default;

  // Seed localStorage before LanguageDetector runs so navigator English does not
  // override the .dk Danish default (or an explicit ?lang= choice).
  try {
    if (!localStorage.getItem('i18nextLng') || langFromQuery()) {
      localStorage.setItem('i18nextLng', initialLng);
    }
  } catch {
    // ignore
  }

  await i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {
        [initialLng]: { translation: primary },
      },
      lng: initialLng,
      fallbackLng: 'en',
      supportedLngs: ['en', 'da'],
      nonExplicitSupportedLngs: true,
      load: 'languageOnly',
      cleanCode: true,
      returnObjects: true,
      interpolation: {
        escapeValue: false,
      },
      detection: {
        // Query + localStorage first; custom .dk default applied in detectInitialLanguage / lng
        order: ['querystring', 'localStorage', 'navigator'],
        lookupQuerystring: 'lang',
        caches: ['localStorage'],
        convertDetectedLanguage: (lng: string) =>
          lng?.toLowerCase().startsWith('da') ? 'da' : 'en',
      },
      react: {
        useSuspense: false,
      },
      saveMissing: true,
      missingKeyHandler: (lngs, ns, key) => {
        if (import.meta.env.DEV) {
          console.warn(`[i18n] Missing translation key: "${key}" for languages: ${lngs}`);
        }
      },
    });

  const normalizedNow: AppLanguage = i18n.language?.toLowerCase().startsWith('da') ? 'da' : 'en';
  if (i18n.language !== normalizedNow) {
    await i18n.changeLanguage(normalizedNow);
  }

  const other: AppLanguage = (i18n.language?.startsWith('da') ? 'en' : 'da');
  const warm = () => {
    localeLoaders[other]()
      .then((mod) => {
        if (!i18n.hasResourceBundle(other, 'translation')) {
          i18n.addResourceBundle(other, 'translation', mod.default, true, true);
        }
      })
      .catch(() => {});
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(warm, { timeout: 4000 });
  } else {
    globalThis.setTimeout(warm, 1200);
  }

  i18n.on('languageChanged', (lng) => {
    const normalized: AppLanguage = lng?.toLowerCase().startsWith('da') ? 'da' : 'en';
    try {
      document.documentElement.lang = normalized;
    } catch {
      // ignore
    }
    if (i18n.hasResourceBundle(normalized, 'translation')) return;
    localeLoaders[normalized]().then((mod) => {
      i18n.addResourceBundle(normalized, 'translation', mod.default, true, true);
      i18n.changeLanguage(normalized);
    });
  });

  try {
    document.documentElement.lang = i18n.language?.startsWith('da') ? 'da' : 'en';
  } catch {
    // ignore
  }

  return i18n;
}

export default i18n;
